/* eslint-disable no-restricted-globals */
// ─────────────────────────────────────────────────────────────────────────
// SERGA // PERSONAL OS — SONIC tray  (sonic.js)
// A right-edge rail that mirrors the FEED tray: hidden on load, summoned by a
// vertical edge handle stacked directly below the FEED handle, sliding in
// horizontally from the right. The handle carries a live EQ indicator that
// animates only while something is playing. Opening SONIC collapses FEED and
// vice-versa — the two rails share the right dock.
//
// All playback is driven by the controller in spotify.js (mock today, real
// Spotify once the account is linked via the brokered OAuth) — this file does
// not change between the two.
// ─────────────────────────────────────────────────────────────────────────

import { createSonicController } from './spotify.js';
import { gIcon } from './glyphs.js';
import { el, trackedListeners } from './dom.js';

const OPEN_KEY = 'serga-sonic-ui';
const RAIL_EVENT = 'serga:rail-open';
const fmt = ms => { const t = Math.max(0, Math.round(ms / 1000)); return Math.floor(t / 60) + ':' + String(t % 60).padStart(2, '0'); };

// procedural cover — two-tone field node, replaced by real art once a track
// carries a `cover` url (Spotify). Motif kept to simple marks.
const MOTIF = {
  disc:    '<circle cx="22" cy="22" r="15"/><circle cx="22" cy="22" r="9"/><circle cx="22" cy="22" r="3"/>',
  diamond: '<rect x="9" y="9" width="26" height="26" transform="rotate(45 22 22)"/><rect x="15" y="15" width="14" height="14" transform="rotate(45 22 22)"/>',
  arc:     '<path d="M5,34 Q22,6 39,34"/><path d="M9,34 Q22,14 35,34"/><path d="M13,34 Q22,22 31,34"/>',
  bars:    '<line x1="12" y1="30" x2="12" y2="16"/><line x1="19" y1="30" x2="19" y2="9"/><line x1="26" y1="30" x2="26" y2="20"/><line x1="33" y1="30" x2="33" y2="12"/>',
  grid:    '<line x1="11" y1="8" x2="11" y2="36"/><line x1="22" y1="8" x2="22" y2="36"/><line x1="33" y1="8" x2="33" y2="36"/><line x1="8" y1="15" x2="36" y2="15"/><line x1="8" y1="29" x2="36" y2="29"/>',
};
function coverHTML(track, size) {
  if (track && track.cover) return `<img class="sc-img" src="${track.cover}" alt="" style="width:${size}px;height:${size}px" />`;
  const h = (track && track.art) ? track.art.h : 196;
  const motif = MOTIF[(track && track.art && track.art.motif) || 'disc'];
  const bg = `linear-gradient(150deg, oklch(0.36 0.07 ${h}), oklch(0.58 0.10 ${(h + 34) % 360}))`;
  return `<span class="scover" style="width:${size}px;height:${size}px;background:${bg}">
      <svg viewBox="0 0 44 44" class="sc-motif">${motif}</svg>
      <span class="sc-c tl"></span><span class="sc-c br"></span>
    </span>`;
}

const TABS = [
  { id: 'queue',     ico: 'queue',   name: 'UP NEXT' },
  { id: 'playlists', ico: 'disc',    name: 'YOUR PLAYLISTS' },
  { id: 'recent',    ico: 'recent',  name: 'RECENTLY PLAYED' },
  { id: 'devices',   ico: 'speaker', name: 'PLAYBACK DEVICE' },
];

// connection chip label per state (broker model: mock = not linked yet)
const CONN_LABEL = { mock: 'LINK', connecting: 'LINKING…', linked: 'LINKED', error: 'RETRY' };

export function createSonic() {
  const root = document.getElementById('sonic');
  if (!root) return { toggle() {}, open() {}, dispose() {} };
  const ctrl = createSonicController();
  const { on, dispose: disposeListeners } = trackedListeners();

  // ── persisted UI state ──────────────────────────────────────────────────
  let ui = { open: false, tab: 'queue' };
  try { ui = { ...ui, ...JSON.parse(localStorage.getItem(OPEN_KEY) || '{}') }; } catch (e) {}
  ui.open = false; // always start hidden on load
  const saveUI = () => { try { localStorage.setItem(OPEN_KEY, JSON.stringify(ui)); } catch (e) {} };

  // ── build chrome ──────────────────────────────────────────────────────────
  root.innerHTML = `
    <button class="sonic-handle" id="sonic-handle" title="open SONIC">
      <span class="sh-eq" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
      <span class="sh-rot">SONIC</span>
      <span class="sh-chev">${'◂'}</span>
    </button>

    <div class="sonic-inner">
      <div class="sonic-bar">
        <span class="sb-id"><span class="sb-diamond"></span>SONIC</span>
        <button class="sb-min" id="sb-min" title="collapse">${'▸'}</button>
      </div>

      <div class="snp-now">
        <span class="sb-cover" id="sb-cover"></span>
        <div class="snp-meta">
          <div class="sb-title" id="sb-title">—</div>
          <div class="sb-sub" id="sb-sub"></div>
          <div class="snp-source" id="snp-source"></div>
        </div>
      </div>

      <div class="sb-scrub">
        <span class="sb-time sb-cur" id="sb-cur">0:00</span>
        <div class="sb-track" id="sb-track"><div class="sb-fill" id="sb-fill"></div><span class="sb-head" id="sb-headknob"></span></div>
        <span class="sb-time sb-dur" id="sb-dur">0:00</span>
      </div>

      <div class="snp-controls">
        <button class="sb-btn" id="sb-shuffle" title="shuffle">${gIcon('shuffle', 16)}</button>
        <button class="sb-btn" id="sb-prev" title="previous">${gIcon('prev', 18)}</button>
        <button class="sb-btn sb-play" id="sb-play" title="play / pause">${gIcon('play', 20)}</button>
        <button class="sb-btn" id="sb-next" title="next">${gIcon('next', 18)}</button>
        <button class="sb-btn" id="sb-repeat" title="repeat">${gIcon('repeat', 16)}</button>
      </div>

      <div class="snp-util">
        <div class="sb-vol">
          <span class="sb-vico">${gIcon('volume', 14)}</span>
          <div class="sb-voltrack" id="sb-voltrack"><div class="sb-volfill" id="sb-volfill"></div></div>
        </div>
        <div class="snp-util-row">
          <button class="sb-device" id="sb-device" title="playback device">
            <span class="sb-dico" id="sb-dico">${gIcon('laptop', 14)}</span>
            <span class="sb-dname" id="sb-dname">—</span>
          </button>
          <button class="sb-conn" id="sb-conn" title="link your Spotify account">
            <span class="sb-cdot" id="sb-cdot"></span><span id="sb-conn-label">LINK</span>
          </button>
        </div>
      </div>

      <div class="snp-tabs" id="snp-tabs">
        ${TABS.map(t => `<button class="snp-tab" data-tab="${t.id}" title="${t.name}">${gIcon(t.ico, 15)}</button>`).join('')}
      </div>
      <div class="snp-body" id="snp-body"></div>
    </div>`;

  // ── refs ──────────────────────────────────────────────────────────────────
  const $ = id => root.querySelector('#' + id);
  const handle = $('sonic-handle');
  const cover = $('sb-cover'), title = $('sb-title'), sub = $('sb-sub'), source = $('snp-source');
  const btnPlay = $('sb-play'), btnShuffle = $('sb-shuffle'), btnRepeat = $('sb-repeat');
  const curT = $('sb-cur'), durT = $('sb-dur'), fill = $('sb-fill'), headKnob = $('sb-headknob'), track = $('sb-track');
  const volFill = $('sb-volfill'), volTrack = $('sb-voltrack');
  const devName = $('sb-dname'), devIco = $('sb-dico'), devBtn = $('sb-device');
  const connBtn = $('sb-conn'), connDot = $('sb-cdot'), connLabel = $('sb-conn-label');
  const snpBody = $('snp-body'), snpTabs = $('snp-tabs');

  // ── open / collapse ──────────────────────────────────────────────────────
  function syncActive() {
    // pause polling unless the rail is open AND the tab is visible
    ctrl.setActive(ui.open && (typeof document === 'undefined' || document.visibilityState === 'visible'));
  }
  function applyUI() {
    root.classList.toggle('open', ui.open);
    // body.sonic-open drives the switch-tab relocation: when SONIC is open, the
    // FEED rail's resting handle moves to SONIC's left edge so the panel never
    // covers it (see styles.css).
    document.body.classList.toggle('sonic-open', ui.open);
    [...snpTabs.children].forEach(b => b.classList.toggle('active', b.dataset.tab === ui.tab));
  }
  function setOpen(v) {
    ui.open = v; applyUI(); saveUI(); syncActive();
    if (v) { renderPanel(ctrl.state); window.dispatchEvent(new CustomEvent(RAIL_EVENT, { detail: 'sonic' })); }
  }
  // mutual exclusivity — collapse when the FEED rail opens
  on(window, RAIL_EVENT, e => { if (e.detail !== 'sonic' && ui.open) setOpen(false); });
  on(document, 'visibilitychange', syncActive);

  on(handle, 'click', () => setOpen(true));
  on($('sb-min'), 'click', () => setOpen(false));
  on(snpTabs, 'click', e => { const b = e.target.closest('.snp-tab'); if (!b) return; ui.tab = b.dataset.tab; applyUI(); saveUI(); renderPanel(ctrl.state); });

  // ── transport wiring ───────────────────────────────────────────────────
  on(btnPlay, 'click', () => ctrl.toggle());
  on($('sb-prev'), 'click', () => ctrl.prev());
  on($('sb-next'), 'click', () => ctrl.next());
  on(btnShuffle, 'click', () => ctrl.toggleShuffle());
  on(btnRepeat, 'click', () => ctrl.cycleRepeat());
  on(connBtn, 'click', () => ctrl.connect());
  on(devBtn, 'click', () => { ui.tab = 'devices'; applyUI(); saveUI(); renderPanel(ctrl.state); });

  bindScrub(track, ratio => ctrl.seek(ratio * ctrl.state.track.durMs));
  bindScrub(volTrack, ratio => ctrl.setVolume(ratio * 100));
  function bindScrub(elm, onSet) {
    let dragging = false;
    const ratioAt = e => { const r = elm.getBoundingClientRect(); return Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)); };
    on(elm, 'pointerdown', e => { dragging = true; elm.setPointerCapture(e.pointerId); onSet(ratioAt(e)); });
    on(elm, 'pointermove', e => { if (dragging) onSet(ratioAt(e)); });
    on(elm, 'pointerup', () => { dragging = false; });
    on(elm, 'pointercancel', () => { dragging = false; });
  }

  // ── renderers ────────────────────────────────────────────────────────────
  function renderScrub(s) {
    const pct = s.track.durMs ? (s.positionMs / s.track.durMs) * 100 : 0;
    fill.style.width = pct + '%';
    headKnob.style.left = pct + '%';
    curT.textContent = fmt(s.positionMs);
    durT.textContent = fmt(s.track.durMs);
  }
  function renderNow(s) {
    cover.innerHTML = coverHTML(s.track, 62);
    title.textContent = s.track.title;
    sub.innerHTML = `<span class="sb-artist">${s.track.artist}</span><span class="sb-dot">·</span>${s.track.album}`;
    source.textContent = s.note || '';
  }
  function renderTransport(s) {
    btnPlay.innerHTML = gIcon(s.status === 'playing' ? 'pause' : 'play', 20);
    root.classList.toggle('playing', s.status === 'playing');
    btnShuffle.classList.toggle('on', s.shuffle);
    btnRepeat.classList.toggle('on', s.repeat !== 'off');
    btnRepeat.classList.toggle('one', s.repeat === 'track');
    volFill.style.width = s.volume + '%';
  }
  function renderDevice(s) {
    const d = s.devices.find(x => x.id === s.deviceId) || s.devices.find(x => x.active);
    if (d) { devName.textContent = d.name; devIco.innerHTML = gIcon(d.type, 14); }
  }
  function renderConn(s) {
    connDot.className = 'sb-cdot ' + s.connection;
    connBtn.className = 'sb-conn ' + s.connection;
    connLabel.textContent = CONN_LABEL[s.connection] || 'LINK';
    connBtn.title = s.note || 'link your Spotify account';
  }

  function trackRow(t) {
    const row = el('div', 'sp-row track');
    row.innerHTML = `${coverHTML(t, 34)}
      <span class="spr-body"><span class="spr-title">${t.title}</span><span class="spr-sub">${t.artist}</span></span>
      <span class="spr-dur">${fmt(t.durMs)}</span>
      <span class="spr-play">${gIcon('play', 12)}</span>`;
    row.addEventListener('click', () => ctrl.playTrack(t));
    return row;
  }
  function headRow(name, tag) {
    return el('div', 'sp-head', `<span class="sph-name">${name}</span><span class="sph-tag">${tag}</span>`);
  }
  function renderPanel(s) {
    snpBody.innerHTML = '';
    const meta = TABS.find(t => t.id === ui.tab);
    if (ui.tab === 'queue') {
      snpBody.appendChild(headRow(meta.name, s.queue.length + ' TRACKS'));
      if (!s.queue.length) snpBody.appendChild(el('div', 'sp-empty', 'QUEUE CLEAR — radio continues from context'));
      s.queue.forEach(t => snpBody.appendChild(trackRow(t)));
    } else if (ui.tab === 'recent') {
      snpBody.appendChild(headRow(meta.name, s.recent.length + ''));
      s.recent.forEach(t => snpBody.appendChild(trackRow(t)));
    } else if (ui.tab === 'playlists') {
      snpBody.appendChild(headRow(meta.name, s.playlists.length + ''));
      s.playlists.forEach(pl => {
        const row = el('div', 'sp-row pl');
        row.innerHTML = `${coverHTML({ cover: pl.cover, art: pl.art }, 38)}
          <span class="spr-body"><span class="spr-title">${pl.name}</span><span class="spr-sub">${pl.note}</span></span>
          <span class="spr-count">${pl.trackIds.length} ${gIcon('disc', 11)}</span>
          <span class="spr-play">${gIcon('play', 12)}</span>`;
        row.addEventListener('click', () => ctrl.loadPlaylist(pl.id));
        snpBody.appendChild(row);
      });
    } else if (ui.tab === 'devices') {
      snpBody.appendChild(headRow(meta.name, s.connection === 'linked' ? 'LIVE' : 'MOCK'));
      if (!s.devices.length) snpBody.appendChild(el('div', 'sp-empty', 'NO DEVICES — open Spotify on a device'));
      s.devices.forEach(d => {
        const active = d.id === s.deviceId || d.active;
        const row = el('div', 'sp-row dev' + (active ? ' active' : ''));
        row.innerHTML = `<span class="dev-ico">${gIcon(d.type, 16)}</span>
          <span class="spr-body"><span class="spr-title">${d.name}</span><span class="spr-sub">${d.type.toUpperCase()}</span></span>
          <span class="dev-state">${active ? 'ACTIVE' : 'SWITCH'}</span>`;
        row.addEventListener('click', () => ctrl.setDevice(d.id));
        snpBody.appendChild(row);
      });
    }
  }

  // ── subscribe ──────────────────────────────────────────────────────────
  const unsub = ctrl.subscribe((s, evt) => {
    renderScrub(s);
    if (evt === 'tick') return;          // tick only moves the playhead — cheap path
    renderNow(s); renderTransport(s); renderDevice(s); renderConn(s);
    if (ui.open) renderPanel(s);
  });

  applyUI();

  return {
    open() { setOpen(true); },
    toggle() { setOpen(!ui.open); },
    controller: ctrl,
    dispose() {
      try { unsub && unsub(); } catch (e) {}
      try { ctrl.dispose && ctrl.dispose(); } catch (e) {}
      document.body.classList.remove('sonic-open');  // don't leak the switch-tab class on unmount
      disposeListeners();
    },
  };
}
